import React, { useState, useRef } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import i18n from 'i18next';
import { useEffect } from 'react';
import * as clientErrTypes from 'config/clientErrorTypes';
import { gameTypes, isPurePrizeGames } from 'config/promoGameTypes';
import { isNoResultPageGames } from 'config/promoGameTypesClientConfig';
import { checkTrackingStatus, submitForm, submitPromo } from 'api/client';
import { gtmPushUserPromoEnter } from 'client/utils/tracking';
import { scrollPromoBodyIntoView } from 'client/utils/scroll';
import {
    successPlayGame,
    setNeedEnterPasscode,
    setCoverPagePhase
} from 'client/redux/modules/userEntryState';
import { updateGameResultRecords } from 'client/redux/modules/userGameResultRecords';
import { showCustomErrDialog, handlers } from 'client/redux/modules/systemErrorDialog';

import useGameActionEnableInfo from 'client/hooks/useGameActionEnableInfo';
import useClientHistory, { RESULT, SIGNUP_FORM, JOIN } from 'client/hooks/useClientHistory';
import useClientErrorHandler, { generateApiErrObj } from 'client/hooks/useClientErrorHandler';
import useSubmitTrackingApi from 'client/hooks/useSubmitTrackingApi';
import useGetParticipantWinner from 'client/hooks/useGetParticipantWinner';
import useSubmitPasscode from 'client/hooks/useSubmitPasscode';

import GameClient from 'client/components/JoinGame/GameClient';
import JoinWrapper from 'client/components/JoinGame/JoinWrapper';
import CoverPage from 'client/components/JoinGame/CoverPage';
import ResultSubmitingMask from 'client/components/JoinGame/ResultSubmitingMask';
import usePasscodeManager from 'client/utils/passcode/usePasscodeManager';
import { getErrorTypeDialogDesc, getPasscodeErrorType, errorTypes } from 'config/passcode';
import DialogReCaptcha from 'components/Dialog/DialogReCaptcha';
import { setReJoin } from 'client/redux/modules/flags';

export default function JoinGame() {
    const dispatch = useDispatch();
    const store = useStore();

    const history = useClientHistory();
    const errHandler = useClientErrorHandler();
    const submitTrackingData = useSubmitTrackingApi();
    const actionEnableState = useGameActionEnableInfo();
    const getParticipantWinner = useGetParticipantWinner();
    const handleSubmitPasscode = useSubmitPasscode();
    const userGameResultRecords = useSelector(s => s.userGameResultRecords);
    const { id: promoId, uuid: promoUUID, type: gameType } = useSelector(s => s.promo);
    const { enableCoverPage = false } = useSelector(s => s.settingPromo);
    const { formId: gameFormId, coverPageContent } = useSelector(s => s.settingGame);
    const { showAfterGame: toSignUpFormAfterGame } = useSelector(s => s.signUpForm);
    const {
        currentEntryId,
        currentEntryUUID,
        hasGameResult,
        hasFilledSignupForm,
        isCoverPagePhase
    } = useSelector(s => s.userEntryState);
    const { enablePasscodeVerify } = useSelector(s => s.promoState);
    const { id: sponsorId } = useSelector(s => s.sponsor);
    const { loginId } = useSelector(s => s.userLoginState);
    const { hasInited, hasUserStateInited } = useSelector(s => s.flags);
    const {
        updatePasscodeList,
        removePasscode,
        continueVerifyPasscode,
        isShowReCaptcha,
        setShowReCaptcha
    } = usePasscodeManager({
        hasVerifiedPasscodeFirstTime: Boolean(
            userGameResultRecords.find(({ id }) => id === currentEntryId)?.flow?.gameResult
        )
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const isLoading = useRef();
    const showCoverPage = gameType !== gameTypes.SIGN_FORM && isCoverPagePhase;

    useEffect(() => {
        if (enableCoverPage) {
            dispatch(setCoverPagePhase(true));
        }

        return () => {
            if (enableCoverPage) {
                dispatch(setCoverPagePhase(false));
            }
        };
    }, [enableCoverPage, dispatch]);

    async function handleGeneralGameFinish(
        { gameValues, headers, additionalErrLog = {} } = {},
        gameErrHandlingCallback = () => {},
        gameActionAfterSubmit = () => Promise.resolve()
    ) {
        // 直接從 Redux store 獲取最新的 currentEntryUUID，避免閉包問題
        const latestState = store.getState();
        const entryUUID = latestState?.userEntryState?.currentEntryUUID || currentEntryUUID;
        const shouldNotCallSubmit =
            !entryUUID ||
            isLoading.current ||
            !actionEnableState.isQualifiedToPlayGame ||
            !actionEnableState.isUserInputReady;

        if (shouldNotCallSubmit) return;
        try {
            setIsSubmitting(true);
            isLoading.current = true;
            let gameResultRecords = userGameResultRecords;
            if (!hasGameResult) {
                const { hasUsablePasscode, passcode, error } = await handleSubmitPasscode();
                if (error) {
                    return;
                }
                await submitTracking();
                const body = { entryUuid: entryUUID, data: gameValues };
                const resp = await (Boolean(gameFormId)
                    ? submitForm(gameFormId, body, headers)
                    : submitPromo({
                          promoUUID,
                          entryUUID: body.entryUuid,
                          body: body.data
                      }));
                if (resp.httpStatus !== 200) {
                    const errorMsgs = gameErrHandlingCallback(resp);
                    if (errorMsgs) {
                        await gameActionAfterSubmit({ success: false, errorMsgs });
                        return {
                            error: true,
                            errorMsgs
                        };
                    } else {
                        if ([errorTypes.ENTRY_NEED_PASSCODE].includes(getPasscodeErrorType(resp))) {
                            dispatch(
                                showCustomErrDialog({
                                    title: i18n.t('label.no_qualify_join'),
                                    desc: getErrorTypeDialogDesc(getPasscodeErrorType(resp)),
                                    handler: handlers.PASSCODE_DENIED
                                })
                            );
                            return;
                        }

                        resp.additionalInfo = JSON.stringify({
                            additionalErrLog
                        });
                        resp.errSerialNo = currentEntryId;
                        throw generateApiErrObj(resp, `${clientErrTypes.JOIN_GAME}-${gameType}`);
                    }
                } else {
                    dispatch(setReJoin(false));
                    // Cookie 只存取還可以使用多次的 passcode，所以當 passcode 只可以使用一次時，需要從 cookie 中移除
                    if (hasUsablePasscode) {
                        updatePasscodeList(passcode);
                    } else {
                        removePasscode(passcode);
                    }
                    await gameActionAfterSubmit({ success: true });

                    gameResultRecords = await handleAfterSubmit();
                }
            }
            await handleSetGameResultDone(gameResultRecords);
            return true;
        } catch (err) {
            errHandler({ err });
            return false;
        } finally {
            setIsSubmitting(false);
            isLoading.current = false;
        }
    }
    async function handlePurePrizeGameFinish() {
        const latestState = store.getState();
        const entryUUID = latestState?.userEntryState?.currentEntryUUID || currentEntryUUID;
        const shouldNotCallSubmit =
            !entryUUID ||
            isLoading.current ||
            !actionEnableState.isQualifiedToPlayGame ||
            !actionEnableState.isUserInputReady;

        if (shouldNotCallSubmit) return;
        try {
            setIsSubmitting(true);
            isLoading.current = true;
            let gameResultRecords = userGameResultRecords;
            if (!hasGameResult) {
                const { hasUsablePasscode, passcode, error } = await handleSubmitPasscode();
                if (error) {
                    return;
                }
                await submitTracking();
                const body = { entryUuid: entryUUID };
                // 因為兑點模組是純獎模租，所以完成遊戲時會 call handlePurePrizeGameFinish，但他是唯一的非 form based 純獎模租
                // 所以建立模組時不會有 formId (沒開填表情況下)，Set game result 是 call submitPromo 而非 submitForm
                const resp = await (Boolean(gameFormId)
                    ? submitForm(gameFormId, body)
                    : submitPromo({
                          promoUUID,
                          entryUUID: body.entryUuid,
                          body: body.data
                      }));
                if (resp.httpStatus !== 200) {
                    if ([errorTypes.ENTRY_NEED_PASSCODE].includes(getPasscodeErrorType(resp))) {
                        dispatch(
                            showCustomErrDialog({
                                title: i18n.t('label.no_qualify_join'),
                                desc: getErrorTypeDialogDesc(getPasscodeErrorType(resp)),
                                handler: handlers.PASSCODE_DENIED
                            })
                        );
                        return;
                    }
                    resp.errType = `${clientErrTypes.JOIN_PURE_PRIZE_GAME}-${gameType}`;
                    resp.errSerialNo = currentEntryId;
                    throw generateApiErrObj(
                        resp,
                        `${clientErrTypes.JOIN_PURE_PRIZE_GAME}-${gameType}`
                    );
                }
                dispatch(setReJoin(false));
                // Cookie 只存取還可以使用多次的 passcode，所以當 passcode 只可以使用一次時，需要從 cookie 中移除
                if (hasUsablePasscode) {
                    updatePasscodeList(passcode);
                } else {
                    removePasscode(passcode);
                }

                gameResultRecords = await handleAfterSubmit();
            }
            await handleSetGameResultDone(gameResultRecords);
            return true;
        } catch (err) {
            errHandler({
                err
            });
            return false;
        } finally {
            setIsSubmitting(false);
            isLoading.current = false;
        }
    }
    async function handleAfterSubmit() {
        const latestState = store.getState();
        const entryUUID = latestState?.userEntryState?.currentEntryUUID || currentEntryUUID;
        const gameResultRecords = await dispatch(updateGameResultRecords());
        dispatch(successPlayGame());
        gtmPushUserPromoEnter({
            loginId,
            entryUuid: entryUUID,
            promoId,
            sponsorId,
            gameType
        });
        return gameResultRecords;
    }
    async function handleSetGameResultDone(gameResultRecords = []) {
        if (
            enablePasscodeVerify &&
            Boolean(gameResultRecords.find(({ id }) => id === currentEntryId)?.flow?.gameResult)
        ) {
            dispatch(setNeedEnterPasscode(false));
        }
        if (hasFilledSignupForm) {
            await getParticipantWinner(gameResultRecords);
            if (!isNoResultPageGames(gameType) && !isPurePrizeGames(gameType)) {
                scrollPromoBodyIntoView();
            }
        } else {
            scrollPromoBodyIntoView();
            if (gameType === gameTypes.PICK_THE_PRIZE || gameType === gameTypes.POINTS_REDEEM) {
                history.push(JOIN);
            } else {
                history.push(Boolean(toSignUpFormAfterGame) ? SIGNUP_FORM : RESULT);
            }
        }
    }

    async function submitTracking() {
        let retry = 0;
        let status = 0;
        const latestState = store.getState();
        const entryUUID = latestState?.userEntryState?.currentEntryUUID || currentEntryUUID;
        do {
            const resp = await checkTrackingStatus({ promoUUID, entriesUUID: entryUUID });
            if (resp.payload.data) {
                status = 1;
            } else {
                const res = await submitTrackingData({ promoUUID, entriesUUID: entryUUID });
                const isSuccess = Boolean(res?.payload?.data?.[0]);
                if (isSuccess) status = 1;
            }

            retry++;
        } while (status === 0 && retry < 3);
    }
    if (!hasInited || !hasUserStateInited) {
        return null;
    }

    return (
        <>
            {isShowReCaptcha && (
                <DialogReCaptcha onComplete={continueVerifyPasscode} onOpen={setShowReCaptcha} />
            )}
            <ResultSubmitingMask show={isSubmitting} />
            <JoinWrapper>
                {showCoverPage ? (
                    <CoverPage
                        content={coverPageContent}
                        onClick={() => dispatch(setCoverPagePhase(false))}
                    />
                ) : (
                    <GameClient
                        onGameFinish={handleGeneralGameFinish}
                        onPurePrizeGameFinish={handlePurePrizeGameFinish}
                    />
                )}
            </JoinWrapper>
        </>
    );
}
